import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Wrench,
  Building2,
  CalendarClock,
  Settings as SettingsIcon,
  Menu,
  LogOut,
  Sparkles,
  Hammer,
  ChevronRight,
  Boxes,
  CalendarDays,
  History,
  ScanLine,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const nav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", id: "nav-dashboard" },
  { to: "/work-orders", icon: ClipboardList, label: "Work Orders", id: "nav-work-orders" },
  { to: "/schedule", icon: CalendarDays, label: "Schedule", id: "nav-schedule" },
  { to: "/assets", icon: Wrench, label: "Assets", id: "nav-assets" },
  { to: "/clients", icon: Building2, label: "Clients", id: "nav-clients" },
  { to: "/maintenance", icon: CalendarClock, label: "Maintenance", id: "nav-maintenance" },
  { to: "/parts", icon: Boxes, label: "Inventory", id: "nav-parts" },
  { to: "/field", icon: Hammer, label: "Field Mode", id: "nav-field" },
  { to: "/scan", icon: ScanLine, label: "Scan QR", id: "nav-scan" },
  { to: "/audit-log", icon: History, label: "Audit Log", id: "nav-audit" },
  { to: "/settings", icon: SettingsIcon, label: "Settings", id: "nav-settings" },
];

const Sidebar = ({ onNavigate }) => {
  const { user, settings, logout } = useAuth();
  return (
    <aside className="h-full bg-zinc-950 text-zinc-100 flex flex-col w-64">
      <div className="p-5 border-b border-zinc-800">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
          {settings?.logo_url ? (
            <img
              src={settings.logo_url}
              alt={settings?.company_name || "FieldOps"}
              className="h-9 w-9 rounded-sm object-cover bg-white"
            />
          ) : (
            <div className="h-9 w-9 rounded-sm bg-orange-500 flex items-center justify-center font-display font-extrabold text-zinc-950">
              {(settings?.company_name || "F")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display font-extrabold text-lg leading-none">
              {settings?.company_name || "FieldOps"}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
              FSM · CRM
            </div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {nav.map(({ to, icon: Icon, label, id }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={id}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                isActive
                  ? "bg-orange-500 text-zinc-950"
                  : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-800">
        <div className="flex items-center gap-3 p-2 rounded-sm bg-zinc-900">
          <Avatar className="h-9 w-9 rounded-sm">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="rounded-sm bg-orange-500 text-zinc-950 font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-orange-400">
              {user?.role}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="text-zinc-400 hover:text-white p-1"
            aria-label="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default function Layout({ children, title, breadcrumbs, actions }) {
  const [open, setOpen] = useState(false);
  const { settings } = useAuth();
  return (
    <div className="min-h-screen flex bg-zinc-50">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="md:hidden p-2 -ml-2"
                  data-testid="open-mobile-nav"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 bg-zinc-950 border-none">
                <Sidebar onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              {breadcrumbs && (
                <div className="flex items-center text-[11px] tracking-wider uppercase text-zinc-500 mb-0.5">
                  {breadcrumbs.map((b, i) => (
                    <span key={i} className="flex items-center">
                      {i > 0 && <ChevronRight className="h-3 w-3 mx-1" />}
                      <span>{b}</span>
                    </span>
                  ))}
                </div>
              )}
              <h1 className="text-base md:text-lg font-display font-bold tracking-tight truncate">
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {settings?.ai_enabled && (
              <Badge
                variant="outline"
                className="hidden sm:flex gap-1 rounded-sm border-orange-300 bg-orange-50 text-orange-800"
                data-testid="ai-badge"
              >
                <Sparkles className="h-3 w-3" /> AI ON
              </Badge>
            )}
            {actions}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6">{children}</main>
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 grid grid-cols-5 z-20">
          {nav.slice(0, 5).map(({ to, icon: Icon, label, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${id}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[10px] font-semibold ${
                  isActive ? "text-orange-600" : "text-zinc-600"
                }`
              }
            >
              <Icon className="h-5 w-5 mb-0.5" />
              {label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
